import { getStore, resetUnreadCountInStore } from 'core_main/store';
import { importAndInitializePubNub } from 'core_main/globals/vendors';
import {
  getSession,
  markSessionAsRead as apiMarkSessionAsRead,
} from 'core_main/api/sessions';
import { ChatSessionsData } from 'core_main/api/sessions/types';
import { handleError } from 'globals/errors';
import { ReadMessage, MarkReadParameters } from './types';
import {
  runListenersForOnUnreadCountChangeEvent,
  validateMarkReadParams,
} from './helpers';

export const publishReadMessage = (
  session: ChatSessionsData,
  skipStoreUpdate = false,
) => {
  const { id } = session;
  const { channels } = session.attributes.pubnub.data;

  const convoMetaChannel = channels.find(
    (channel) => channel.type === 'meta-conversation',
  );

  return importAndInitializePubNub().then((module) => {
    const pubnub = module;
    const store = getStore();

    if (store.isPubNubConnected && convoMetaChannel) {
      const readMessage: ReadMessage = {
        message: {
          eventType: 'read',
        },
        channel: convoMetaChannel.name,
      };

      return pubnub
        .getMetaPubNub()
        ?.instance?.publish(readMessage)
        .then(() => {
          if (!skipStoreUpdate) {
            resetUnreadCountInStore(id);
            runListenersForOnUnreadCountChangeEvent(id);
          }
        });
    }

    return undefined;
  });
};

/**
 * Marks a session as read by marking all unread messages and CSATs in the session as read.
 *
 * This function:
 * - Validates the sessionId parameter
 * - Retrieves the session from the store or fetches it if not available
 * - Publishes a read message to PubNub in parallel (fire-and-forget, doesn't block)
 * - Calls the API to mark the session as read (this is the critical path)
 * - Updates the store and triggers unread count change listeners after the API call succeeds
 *
 * Note: All unread messages in the session will be marked as read, including CSAT if there is one.
 * The API call is the source of truth, and store updates only happen after it succeeds.
 *
 * @param sessionId - The ID of the session to mark as read
 * @returns Promise that resolves when the session is successfully marked as read, or rejects with an error
 * @throws Will reject the promise if the sessionId is invalid or if the API call fails
 */
export const markSessionAsRead = (sessionId: string): Promise<void> => {
  const store = getStore();
  const { sessions } = store;

  const validateMarkReadParamsError = validateMarkReadParams(sessionId);
  if (validateMarkReadParamsError) {
    return Promise.reject(validateMarkReadParamsError);
  }

  return Promise.resolve(sessions[sessionId])
    .then((sessionFromStore) => {
      if (sessionFromStore) return Promise.resolve(sessionFromStore);
      return getSession(sessionId).then((res) => res.data);
    })
    .then((session) => {
      // Publish to PubNub in parallel (fire-and-forget, doesn't block)
      publishReadMessage(session, true).catch(() => {
        // Silently handle PubNub failures - API is the source of truth
      });

      // API call is the critical path - store updates only happen after it succeeds
      return apiMarkSessionAsRead(sessionId);
    })
    .then(() => {
      // Update store and trigger listeners only after API call succeeds
      resetUnreadCountInStore(sessionId);
      runListenersForOnUnreadCountChangeEvent(sessionId);
      // Return void on success
      return undefined;
    })
    .catch((error) => {
      handleError(error, {
        message: 'Failed to mark session as read',
      });
      return Promise.reject(error);
    });
};

/**
 * @deprecated This function is deprecated. Use {@link markSessionAsRead} instead.
 *
 * Marks a session as read. This is a backward-compatible wrapper that uses
 * the new markSessionAsRead functionality internally.
 *
 * Note: The messageIds parameter is accepted for backward compatibility but
 * is no longer used. When calling this function, all unread messages in the session
 * will be marked as read, including CSAT if there is one.
 *
 * @param parameters - MarkReadParameters containing conversationId and optional messageIds
 * @returns Promise that resolves when the session is marked as read
 */
export const markRead = (parameters: MarkReadParameters): Promise<void> => {
  const sessionId = parameters?.conversationId;
  // messageIds parameter is ignored - new endpoint marks entire session as read

  const validateMarkReadParamsError = validateMarkReadParams(sessionId);
  if (validateMarkReadParamsError) {
    return Promise.reject(validateMarkReadParamsError);
  }

  // Use the new markSessionAsRead functionality which marks all messages and CSATs
  return markSessionAsRead(sessionId);
};
